import { BusinessVerificationApiError } from 'src/app/classes/models/business-verification-api-error';
import { InsuranceAgent } from 'src/app/classes/models/insurance-agent';
import { Note } from 'src/app/classes/models/note';
import { SmallBusinessOption } from 'src/app/classes/models/small-business-option';
import { VendorTradeState } from 'src/app/classes/models/vendor-trade-state';
import { CompanyType } from 'src/app/interfaces/company-type';
import { Jurisdiction } from 'src/app/interfaces/jurisdiction';
import { ModelDataType } from 'src/app/interfaces/model-data-type';
import { RegistrationProgress } from 'src/app/interfaces/models/registration-progress';
import { VendorRanking } from 'src/app/interfaces/models/vendor-ranking';
import { StockExchange } from 'src/app/interfaces/stock-exchanges';
import { NonNullableInterface } from 'src/app/interfaces/utility/non-nullable-interface';

import { Address } from './address';
import { BaseModel } from './base-model';
import { Bundle } from './bundle';
import { ChangeHistory } from './change-history';
import { CompanyVendor } from './company-vendor';
import { CompanyVendorPayment } from './company-vendor-payment';
import { ComplianceFinding } from './compliance-finding';
import { Contact } from './contact';
import { File as ViveFile } from './file';
import { InsuranceCertificate } from './insurance-certificate';
import { InsurancePolicy } from './insurance-policy';
import { InvitationContact } from './invitation-contact';
import { Owner } from './owner';
import { PaymentCustomer } from './payment-customer';
import { PaymentIdentifier } from './payment-identifier';
import { ProposalRequestVendor } from './proposal-request-vendor';
import { Site } from './site';
import { VendorProfile } from './vendor-profile';
import { VendorReview } from './vendor-review';
import { VendorTrade } from './vendor-trade';

export class Vendor extends BaseModel {
  static override columnMap = new Map<
    string,
    { property: string; type: 'column'; dataType: ModelDataType }
  >();
  static override associationMap = new Map<
    string,
    { property: string; type: 'association'; model: string }
  >();
  static override aliasedColumnMap = new Map<
    string,
    { property: string; type: 'alias' }
  >();

  static VendorProperties: (
    | {
        property: keyof Vendor;
        type: 'column';
        dataType: ModelDataType;
      }
    | {
        property: keyof Vendor;
        type: 'alias';
      }
    | {
        property: keyof Vendor;
        type: 'association';
        model: string;
      }
  )[] = [
    { property: 'id', type: 'column', dataType: 'number' },
    { property: 'apiSource', type: 'column', dataType: 'string' },
    { property: 'apiKey', type: 'column', dataType: 'string' },
    { property: 'legalName', type: 'column', dataType: 'string' },
    { property: 'logo', type: 'column', dataType: 'string' },
    { property: 'LogoFileId', type: 'column', dataType: 'number' },
    { property: 'dba', type: 'column', dataType: 'string' },
    { property: 'ein', type: 'column', dataType: 'string' },
    { property: 'ssn', type: 'column', dataType: 'string' },
    { property: 'bsn', type: 'column', dataType: 'string' },
    { property: 'rsn', type: 'column', dataType: 'string' },
    { property: 'usesSIN', type: 'column', dataType: 'boolean' },
    { property: 'isWorkersCompExempt', type: 'column', dataType: 'boolean' },
    { property: 'isPubliclyTraded', type: 'column', dataType: 'boolean' },
    { property: 'qtyEmployees', type: 'column', dataType: 'number' },
    { property: 'employsSubContractors', type: 'column', dataType: 'boolean' },
    { property: 'stockExchange', type: 'column', dataType: 'string' },
    { property: 'stockSymbol', type: 'column', dataType: 'string' },
    { property: 'yearEstablished', type: 'column', dataType: 'number' },
    { property: 'hasComplexOwnership', type: 'column', dataType: 'boolean' },
    { property: 'website', type: 'column', dataType: 'string' },
    { property: 'linkedin', type: 'column', dataType: 'string' },
    { property: 'facebook', type: 'column', dataType: 'string' },
    { property: 'companyType', type: 'column', dataType: 'string' },
    { property: 'jurisdiction', type: 'column', dataType: 'string' },
    { property: 'llcType', type: 'column', dataType: 'string' },
    { property: 'address1', type: 'column', dataType: 'string' },
    { property: 'address2', type: 'column', dataType: 'string' },
    { property: 'city', type: 'column', dataType: 'string' },
    { property: 'state', type: 'column', dataType: 'string' },
    { property: 'zip', type: 'column', dataType: 'string' },
    { property: 'country', type: 'column', dataType: 'string' },
    { property: 'latitude', type: 'column', dataType: 'string' },
    { property: 'longitude', type: 'column', dataType: 'string' },
    { property: 'phone', type: 'column', dataType: 'string' },
    { property: 'phoneExt', type: 'column', dataType: 'string' },
    { property: 'phone2', type: 'column', dataType: 'string' },
    { property: 'fax', type: 'column', dataType: 'string' },
    { property: 'mailingAddress1', type: 'column', dataType: 'string' },
    { property: 'mailingAddress2', type: 'column', dataType: 'string' },
    { property: 'mailingCity', type: 'column', dataType: 'string' },
    { property: 'mailingState', type: 'column', dataType: 'string' },
    { property: 'mailingZip', type: 'column', dataType: 'string' },
    { property: 'serviceArea', type: 'column', dataType: 'string' },
    { property: 'tradeStates', type: 'column', dataType: 'string' },
    { property: 'invitedByCompanyId', type: 'column', dataType: 'number' },
    { property: 'isOnSite', type: 'column', dataType: 'boolean' },
    { property: 'isExempt', type: 'column', dataType: 'boolean' },
    { property: 'isProfessional', type: 'column', dataType: 'boolean' },
    { property: 'isSupplier', type: 'column', dataType: 'boolean' },
    { property: 'feesWaived', type: 'column', dataType: 'boolean' },
    { property: 'renewalDate', type: 'column', dataType: 'date' },
    { property: 'vendorType', type: 'column', dataType: 'string' },
    { property: 'inviteCode', type: 'column', dataType: 'string' },
    { property: 'registrationStatus', type: 'column', dataType: 'string' },
    { property: 'companyDescription', type: 'column', dataType: 'string' },
    { property: 'experienceDescription', type: 'column', dataType: 'string' },
    {
      property: 'tradeOrganizationsDescription',
      type: 'column',
      dataType: 'string'
    },
    { property: 'affiliationsDescription', type: 'column', dataType: 'string' },
    { property: 'syncronizePayments', type: 'column', dataType: 'boolean' },
    { property: 'allowDuplicateTaxId', type: 'column', dataType: 'boolean' },
    { property: 'isArchived', type: 'column', dataType: 'boolean' },
    { property: 'allowAfterHoursService', type: 'column', dataType: 'boolean' },
    { property: 'afterHoursPhone', type: 'column', dataType: 'string' },
    { property: 'afterHoursPhoneExt', type: 'column', dataType: 'string' },
    { property: 'createdAt', type: 'column', dataType: 'date' },
    { property: 'updatedAt', type: 'column', dataType: 'date' },

    { property: 'vendorTrades', type: 'alias' },
    { property: 'vendorTradeCategories', type: 'alias' },
    { property: 'hasFullPackageClient', type: 'alias' },
    { property: 'hasBusinessReportData', type: 'alias' },
    { property: 'hasOwnerReportData', type: 'alias' },
    { property: 'hasSSN', type: 'alias' },
    { property: 'hasVendorPaidForAnyClient', type: 'alias' },
    { property: 'hasMultipleClients', type: 'alias' },
    { property: 'vendorRiskLevel', type: 'alias' },
    { property: 'lastRanBusinessVerificationReportDate', type: 'alias' },
    { property: 'hasLicenseRequirement', type: 'alias' },
    { property: 'invoiceIds', type: 'alias' },
    { property: 'activeTransactionNumbers', type: 'alias' },
    { property: 'hasProfessionalTrade', type: 'alias' },
    { property: 'hasNonProfessionalTrade', type: 'alias' },
    { property: 'hasProfessionalOrNonProfessionalTrade', type: 'alias' },
    { property: 'hasSignedAnyVendorAgreement', type: 'alias' },
    { property: 'vendorDistanceFromCities', type: 'alias' },
    { property: 'maskedSSN', type: 'alias' },
    { property: 'isVisibleForRegistration', type: 'alias' },
    { property: 'isApprovedWithAtLeastOneActiveClient', type: 'alias' },
    { property: 'vendorTradeStates', type: 'alias' },
    { property: 'needsToCompleteRegistration', type: 'alias' },
    { property: 'needsToRenew', type: 'alias' },

    { property: 'Addresses', model: 'Address', type: 'association' },
    { property: 'StreetAddress', model: 'StreetAddress', type: 'association' },
    {
      property: 'MailingAddress',
      model: 'MailingAddress',
      type: 'association'
    },
    {
      property: 'RemitToAddress',
      model: 'RemitToAddress',
      type: 'association'
    },
    { property: 'CompanyVendors', model: 'CompanyVendor', type: 'association' },
    { property: 'Owners', model: 'Owner', type: 'association' },
    { property: 'Contacts', model: 'Contact', type: 'association' },
    {
      property: 'InsuranceAgents',
      model: 'InsuranceAgent',
      type: 'association'
    },
    {
      property: 'InsuranceCertificates',
      model: 'InsuranceCertificate',
      type: 'association'
    },
    {
      property: 'InsurancePolicies',
      model: 'InsurancePolicy',
      type: 'association'
    },
    { property: 'VendorRankings', model: 'VendorRanking', type: 'association' },
    {
      property: 'VendorTradeStates',
      model: 'VendorTradeState',
      type: 'association'
    },
    { property: 'VendorTrades', model: 'VendorTrade', type: 'association' },
    { property: 'VendorReviews', model: 'VendorReview', type: 'association' },
    {
      property: 'RegistrationProgresses',
      model: 'RegistrationProgress',
      type: 'association'
    },
    {
      property: 'SmallBusinessOptions',
      model: 'SmallBusinessOption',
      type: 'association'
    },
    { property: 'Notes', model: 'Note', type: 'association' },
    {
      property: 'CompanyVendorPayments',
      model: 'CompanyVendorPayment',
      type: 'association'
    },
    {
      property: 'ChangeHistories',
      model: 'ChangeHistory',
      type: 'association'
    },
    { property: 'Sites', model: 'Site', type: 'association' },
    {
      property: 'InvitationContacts',
      model: 'InvitationContact',
      type: 'association'
    },
    {
      property: 'PaymentCustomers',
      model: 'PaymentCustomer',
      type: 'association'
    },
    {
      property: 'StripePaymentCustomer',
      model: 'StripePaymentCustomer',
      type: 'association'
    },
    {
      property: 'ProposalRequestVendors',
      model: 'ProposalRequestVendor',
      type: 'association'
    },
    {
      property: 'BusinessVerificationApiErrors',
      model: 'BusinessVerificationApiError',
      type: 'association'
    },
    {
      property: 'LogoFile',
      model: 'ViveFile',
      type: 'association'
    }
  ];

  static {
    Vendor.VendorProperties.forEach((propertyObject) => {
      const { property, type } = propertyObject;
      if (type === 'column') {
        Vendor.columnMap.set(property, propertyObject);
      }
      if (type === 'alias') {
        Vendor.aliasedColumnMap.set(
          property,
          propertyObject as { property: string; type: 'alias' }
        );
      }
      if (type === 'association') {
        Vendor.associationMap.set(
          property,
          propertyObject as {
            property: string;
            type: 'association';
            model: string;
          }
        );
      }
    });
  }

  constructor(VendorObject: Partial<Vendor>) {
    super();

    Vendor.VendorProperties.forEach((propertyObject) => {
      const { property } = propertyObject;
      const value = VendorObject[property];
      if (value !== undefined) {
        Object.assign(this, { [property]: value });
      }
    });
  }

  id?: number;
  apiSource?: string | null;
  apiKey?: string | null;
  legalName?: string;
  logo?: string | null;
  LogoFileId?: number | null;
  dba?: string | null;
  ein?: string | null;
  ssn?: string | null;
  bsn?: string | null;
  rsn?: string | null;
  usesSIN?: boolean;
  isWorkersCompExempt?: boolean;
  isPubliclyTraded?: boolean;
  qtyEmployees?: number;
  employsSubContractors?: boolean;
  stockExchange?: StockExchange | null;
  stockSymbol?: string | null;
  yearEstablished?: number | null;
  hasComplexOwnership?: boolean;
  website?: string | null;
  linkedin?: string | null;
  facebook?: string | null;
  companyType?: CompanyType | null;
  jurisdiction?: Jurisdiction | null;
  llcType?: 'C' | 'S' | 'P' | 'disregardedEntity' | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: 'US' | 'CA';
  latitude?: string | null;
  longitude?: string | null;
  phone?: string | null;
  phoneExt?: string | null;
  phone2?: string | null;
  fax?: string | null;
  mailingAddress1?: string | null;
  mailingAddress2?: string | null;
  mailingCity?: string | null;
  mailingState?: string | null;
  mailingZip?: string | null;
  serviceArea?: string | null;
  tradeStates?: string | null;
  invitedByCompanyId?: number | null;
  isOnSite?: boolean;
  isExempt?: boolean;
  isProfessional?: boolean;
  isSupplier?: boolean;
  feesWaived?: boolean;
  renewalDate?: Date | null;
  vendorType?: string | null;
  inviteCode?: string | null;
  registrationStatus?:
    | 'created'
    | 'invited'
    | 'needpayment'
    | 'businessInfoComplete'
    | 'stepOnePartBComplete'
    | 'stepOnePartCComplete'
    | 'stepOneComplete'
    | 'stepTwoComplete'
    | 'stepThreeComplete'
    | 'stepFourComplete'
    | 'stepFiveComplete'
    | 'pendingPayment'
    | 'complete';
  companyDescription?: string | null;
  experienceDescription?: string | null;
  tradeOrganizationsDescription?: string | null;
  affiliationsDescription?: string | null;
  syncronizePayments?: boolean;
  allowDuplicateTaxId?: boolean;
  isArchived?: boolean;
  allowAfterHoursService?: boolean;
  afterHoursPhone?: string | null;
  afterHoursPhoneExt?: string | null;
  createdAt?: Date;
  updatedAt?: Date;

  // Aliased columns
  vendorTrades?: string;
  vendorTradeCategories?: string;
  hasFullPackageClient?: boolean | 1 | 0;
  hasSSN?: boolean | 1 | 0;
  hasVendorPaidForAnyClient?: boolean | 1 | 0;
  hasMultipleClients?: boolean | 1 | 0;
  vendorRiskLevel?: number;
  lastRanBusinessVerificationReportDate?: Date | null;
  lastRanOwnerVerificationReportDate?: Date | null;
  hasBusinessReportData?: boolean;
  hasOwnerReportData?: boolean;
  hasLicenseRequirement?: boolean;
  invoiceIds?: string;
  activeTransactionNumbers?: string;
  hasProfessionalTrade?: boolean;
  hasNonProfessionalTrade?: boolean;
  hasProfessionalOrNonProfessionalTrade?: boolean;
  hasSignedAnyVendorAgreement?: boolean;
  vendorDistanceFromCities?: string;
  maskedSSN?: string;
  isVisibleForRegistration?: boolean;
  isApprovedWithAtLeastOneActiveClient?: boolean;
  vendorTradeStates?: string;
  needsToCompleteRegistration?: boolean;
  needsToRenew?: boolean;

  Addresses?: Address[];
  StreetAddress?: Address;
  MailingAddress?: Address;
  RemitToAddress?: Address;
  CompanyVendors?: CompanyVendor[];
  Owners?: Owner[];
  Contacts?: Contact[];
  ComplianceContact?: Contact;
  BidContact?: Contact;
  BillingContact?: Contact;
  InsuranceAgents?: InsuranceAgent[];
  InsuranceCertificates?: InsuranceCertificate[];
  InsurancePolicies?: InsurancePolicy[];
  VendorRankings?: VendorRanking[];
  VendorTradeStates?: VendorTradeState[];
  VendorTrades?: VendorTrade[];
  VendorReviews?: VendorReview[];
  RegistrationProgresses?: RegistrationProgress[];
  SmallBusinessOptions?: Required<
    NonNullableInterface<Pick<SmallBusinessOption, 'id' | 'VendorId' | 'type'>>
  >[];
  Notes?: Note[];
  CompanyVendorPayments?: CompanyVendorPayment[];
  PaymentIdentifiers?: PaymentIdentifier[];
  Bundle?: Bundle;
  ChangeHistories?: ChangeHistory[];
  VendorRanking?: VendorRanking;
  ComplianceFinding?: ComplianceFinding | null;
  VendorProfile?: VendorProfile;
  Sites?: Site[] = [];
  InvitationContacts?: InvitationContact[] = [];
  PaymentCustomers?: PaymentCustomer[] = [];
  StripePaymentCustomer?: PaymentCustomer;
  ProposalRequestVendors?: ProposalRequestVendor[];
  BusinessVerificationApiErrors?: BusinessVerificationApiError[];
  LogoFile?: ViveFile;
}
